/* eslint-disable max-statements */
/* eslint-disable camelcase */
import React, { useEffect, useMemo, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { useNavigate } from 'react-router-dom';
import { useSelector } from 'react-redux';
import { toast } from 'react-toastify';
import { Tooltip } from 'primereact/tooltip';
import { Tooltip as ReactTooltip } from 'react-tooltip';
import { selectCurrentEmpid, selectCurrentUser } from '../../../../pages/login/authSlice';
import { useGetPrepareAssociateMetricsQuery, useGetCapDailyMetricSummaryQuery, useCalculateNextBestActionAssociateMutation, useUpdateNbaActionedMutation } from '../../coachingActionPlanSlice';
import { useStartCoachingActionPlanMutation } from '../../coachingActionSlice';
import {
    useGetMonthlyMetricHistoryQuery,
} from '../../../../pages/metricdashboard/metricDashboardSlice';
import { MetricTimeSeriesBarGraph } from '../../../../components/metricTimeSeriesBarGraph/MetricTimeSeriesBarGraph';
import CoachingShoutOut from '../../../../components/coachingShoutOut/coachingShoutOUt';
import { PrepareAssociate, PrepareAssociateMetric } from '../prepare-types';
import { getMetricDirectionImage } from '../../../../utils/metricFormatters';
import { metricValueFormat } from '../../../../utils/metricValueFormat';
import { CoachingEntityTypes, CoachingTabs } from '../../../../utils/typeConstants';
import { MetricColors, TieredMetricColors } from '../../../../components/metricMonthlySummaryCard/GradientDefs';
import { MetricGaugeVisual } from '../../../../components/metricGaugeVisual/MetricGaugeVisual';
import { isTieredScorecard, getMeetsTarget, getExceedsTarget, MetricTargetTier, getTieredStatusVisual } from '../../../../utils/metricAttainment';
import standardMetricsIcon from '../../../../assets/standardMetrics.png';
import additionalMetricsIcon from '../../../../assets/additionalMetrics.png';
import NewConversationLog from '../../../coaching/conversationLogs/newConversationLog/NewConversationLog';
import { TieredTargetIcon } from '../../../../components/metricMonthlySummaryCard/tieredMetricIcons';

// Must match the key in TeamLeadCAP.tsx
const CAP_NAV_TOKEN_KEY = 'cap_nav_intent';

interface AssociateExpandedViewProps {
    associate: PrepareAssociate;
    scorecardVersionId: number;
    readonly?: boolean;
}

// coaching_next_best_action_type_id constants
const TYPE_START_COACHING = 1;
const TYPE_RECOGNIZE = 2;
const TYPE_FOLLOW_UP = 3;
const TYPE_REVIEW = 4;
// const TYPE_NO_DATA     = 5  — no action, button hidden
const TYPE_RECOGNIZED = 6;

// coaching_action_plan_status_id constants
const CAP_STATUS_DRAFT = 1;
// const CAP_STATUS_OPEN  = 2;

export const AssociateExpandedView: React.FC<AssociateExpandedViewProps> = ({ associate, scorecardVersionId, readonly }) => {
    const navigate = useNavigate();
    const authorEmployeeNumber = useSelector(selectCurrentEmpid)?.toString();
    const [startCoachingActionPlan, { isLoading: isStarting }] = useStartCoachingActionPlanMutation();
    const [startError, setStartError] = useState<string | null>(null);
    const [shoutoutVisible, setShoutoutVisible] = useState(false);
    const [shoutoutResult, setShoutoutResult] = useState<any>(null);
    const [recognizingMetric, setRecognizingMetric] = useState<PrepareAssociateMetric | null>(null);
    const [updateNbaActioned] = useUpdateNbaActionedMutation();
    const [calculateNextBestAction] = useCalculateNextBestActionAssociateMutation();
    const [activeTab, setActiveTab] = useState<'standard' | 'additional'>('standard');
    const [viewState, setViewState] = useState<string>('new');
    const [showNewConversationModal, setShowNewConversationModal] = useState(false);
    const loggedUser = useSelector(selectCurrentEmpid);
    const loggedEmpUserName = useSelector(selectCurrentUser);
    const [selectedScorecardMetricId, setSelectedScorecardMetricId] = useState<number | undefined>(undefined);

    // ── Additional Actions dropdown state ─────────────────────────────────────
    const [openDropdownId, setOpenDropdownId] = useState<number | null>(null);
    const [dropdownPos, setDropdownPos] = useState<{ top: number; left: number } | null>(null);
    const dropdownRef = useRef<HTMLDivElement | null>(null);

    // Close dropdown on any outside click
    useEffect(() => {
        const handleOutsideClick = (e: MouseEvent) => {
            if ((e.target as HTMLElement).closest('.additional-actions-btn')) return;
            if (dropdownRef.current && dropdownRef.current.contains(e.target as Node)) return;
            setOpenDropdownId(null);
            setDropdownPos(null);
        };
        document.addEventListener('mousedown', handleOutsideClick);
        return () => document.removeEventListener('mousedown', handleOutsideClick);
    }, []);

    useEffect(() => {
        const handleScroll = () => {
            setOpenDropdownId(null);
            setDropdownPos(null);
        };
        window.addEventListener('scroll', handleScroll, true);
        return () => window.removeEventListener('scroll', handleScroll, true);
    }, []);

    useEffect(() => {
        if (shoutoutResult === 'Shoutout saved successfully') {
            updateNbaActioned({
                coachingNextBestActionId: associate.coaching_next_best_action_id,
                scorecardMetricId: recognizingMetric?.scorecard_metric_id,
            })
                .unwrap()
                .then(() => {
                    calculateNextBestAction({
                        scorecardVersionId,
                        employeeNumber: associate.employee_number,
                    })
                        .unwrap()
                        .then(() => {
                            setShoutoutResult(null);
                            setRecognizingMetric(null);
                        })
                        .catch((nbaError: any) => {
                            console.error('NBA recalculation failed:', nbaError);
                            setShoutoutResult(null);
                        });
                })
                .catch((error: any) => {
                    console.error('Failed to update NBA actioned:', error);
                    toast.error('Failed to update action status.');
                    setShoutoutResult(null);
                });
        }
    }, [shoutoutResult]);

    // ── Metrics list ──────────────────────────────────────────────────────────
    const { data: metricsResponse, isLoading: metricsLoading } = useGetPrepareAssociateMetricsQuery(
        associate.coaching_next_best_action_id,
        { skip: !associate.coaching_next_best_action_id }
    );

    const metrics: PrepareAssociateMetric[] = metricsResponse?.data?.metrics || [];

    const standardMetrics = metrics.filter((m) => m.is_payout_metric === true);
    const additionalMetrics = metrics.filter((m) => m.is_payout_metric === false);
    const displayedMetrics = activeTab === 'standard' ? standardMetrics : additionalMetrics;

    const defaultMetricIndex = metrics.findIndex((m) => m.scorecard_metric_id === associate.scorecard_metric_id);
    const [selectedMetricId, setSelectedMetricId] = useState<number>(associate.scorecard_metric_id);
    const [selectedMetricIndex, setSelectedMetricIndex] = useState<number>(defaultMetricIndex >= 0 ? defaultMetricIndex : 0);
    const selectedMetric = metrics.find((m) => m.scorecard_metric_id === selectedMetricId) ?? metrics[0];

    const employeeNumberInt = parseInt(associate.employee_number, 10);

    // ── Daily metrics ─────────────────────────────────────────────────────────
    const { data: dailyMetricsResponse } = useGetCapDailyMetricSummaryQuery(
        {
            employeeNumber: employeeNumberInt,
            scorecardVersionId,
            scorecardRoleId: 1,
            startDate: selectedMetric?.start_date ?? null,
            endDate: selectedMetric?.end_date ?? null,
        },
        { skip: !selectedMetric || !employeeNumberInt }
    );

    const currentYear = new Date().getFullYear();
    const currentMonth = new Date().getMonth() + 1;

    // ── Monthly metric history ────────────────────────────────────────────────
    const { data: metricHistoryResponse } = useGetMonthlyMetricHistoryQuery(
        { employeenumber: employeeNumberInt, year: currentYear, month: currentMonth, scorecardVersionId },
        { skip: !selectedMetric || !employeeNumberInt }
    );

    // ── Memoized derived data — prevents chart reload on dropdown open/close ──
    const selectedDailyElement = useMemo(() => {
        const matching = dailyMetricsResponse?.data?.filter(
            (el: any) => el.label === selectedMetric?.metric_name
        ) ?? [];
        if (matching.length === 0) return null;
        return {
            ...matching[0],
            dailyMetricSummaryList: matching
                .flatMap((el: any) => el.dailyMetricSummaryList ?? [])
                .sort((a: any, b: any) =>
                    new Date(b.result_date).getTime() - new Date(a.result_date).getTime()
                ),
        };
    }, [dailyMetricsResponse, selectedMetric?.metric_name]);

    const filteredHistoryData = useMemo(
        () => metricHistoryResponse?.data?.filter(
            (el: any) => el.label === selectedMetric?.metric_name
        ),
        [metricHistoryResponse, selectedMetric?.metric_name]
    );

    // ── Navigate to Analyze step (step 1) with existing Draft plan ────────────
    const navigateToAnalyzeStep = async (metric: PrepareAssociateMetric) => {
        setStartError(null);
        try {
            const response = await startCoachingActionPlan({
                employee_number: associate.employee_number,
                author_employee_number: authorEmployeeNumber,
                scorecard_metric_id: metric.scorecard_metric_id,
                coaching_next_best_action_id: associate.coaching_next_best_action_id,
                session_start_date: new Date().toISOString(),
                metric_current_value: metric.metric_value,
                metric_target_value: metric.target,
                corrective_action_id: null,
            }).unwrap();
            console.log('Start Coaching response (Draft resume):', response);

            window.scrollTo(0, 0);
            sessionStorage.setItem(CAP_NAV_TOKEN_KEY, 'true');
            navigate('/coachingActionPlan', {
                state: {
                    activeStep: 1,
                    coachingActionPlanId: metric.coaching_action_plan_id,
                    coachingActionPlanSessionId: metric.latest_session_id,
                    employeeNumber: associate.employee_number,
                    employeeName: associate.employee_name,
                    scorecardVersionId,
                    scorecardMetricId: metric.scorecard_metric_id,
                    coachingNextBestActionId: associate.coaching_next_best_action_id,
                    metricName: metric.metric_name,
                    metricCurrentValue: metric.metric_value ?? null,
                    metricTargetValue: metric.target ?? null,
                    metricTypeId: metric.metric_type_id,
                    metricTypeName: metric.metric_type_name,
                    metricDirectionId: metric.metric_direction_id,
                    metricIndex: selectedMetricIndex,
                    dailyData: selectedDailyElement ?? null,
                    historyData: filteredHistoryData ?? [],
                    metricStartDate: metric.start_date ?? null,
                    metricEndDate: metric.end_date ?? null,
                },
            });
        } catch {
            setStartError('Failed to start coaching session from Draft State');
        }
    };

    // ── Navigate directly to Coach step (step 2) ─────────────────────────────
    const navigateToCoachStep = (metric: PrepareAssociateMetric, isReviewSession = false) => {
        window.scrollTo(0, 0);
        sessionStorage.setItem(CAP_NAV_TOKEN_KEY, 'true');
        navigate('/coachingActionPlan', {
            state: {
                activeStep: 2,
                isReviewSession,
                coachingActionPlanId: metric.coaching_action_plan_id,
                coachingActionPlanSessionId: metric.latest_session_id,
                employeeNumber: associate.employee_number,
                employeeName: associate.employee_name,
                scorecardVersionId,
                scorecardMetricId: metric.scorecard_metric_id,
                coachingNextBestActionId: associate.coaching_next_best_action_id,
                metricName: metric.metric_name,
                metricCurrentValue: metric.metric_value ?? null,
                metricTargetValue: metric.target ?? null,
                metricTypeId: metric.metric_type_id,
                metricTypeName: metric.metric_type_name,
                metricDirectionId: metric.metric_direction_id,
                metricIndex: selectedMetricIndex,
                dailyData: selectedDailyElement ?? null,
                historyData: filteredHistoryData ?? [],
                metricStartDate: metric.start_date ?? null,
                metricEndDate: metric.end_date ?? null,
            },
        });
    };

    // ── Navigate to FollowUp step ─────────────────────────────────────────────
    const navigateToFollowUp = (metric: PrepareAssociateMetric) => {
        window.scrollTo(0, 0);
        sessionStorage.setItem(CAP_NAV_TOKEN_KEY, 'true');
        navigate('/coachingActionPlan', {
            state: {
                activeStep: 3,
                coachingActionPlanId: metric.coaching_action_plan_id,
                employeeNumber: associate.employee_number,
                employeeName: associate.employee_name,
                scorecardVersionId,
                scorecardMetricId: selectedMetric?.scorecard_metric_id,
                metricName: selectedMetric?.metric_name,
                metricCurrentValue: selectedMetric?.metric_value ?? null,
                metricTargetValue: selectedMetric?.target ?? null,
                metricTypeId: selectedMetric?.metric_type_id,
                metricTypeName: selectedMetric?.metric_type_name,
                metricDirectionId: selectedMetric?.metric_direction_id,
                metricIndex: selectedMetricIndex,
                dailyData: selectedDailyElement ?? null,
                historyData: filteredHistoryData ?? [],
            },
        });
    };

    // ── Start Coaching handler ────────────────────────────────────────────────
    const handleStartCoaching = async (metric: PrepareAssociateMetric) => {
        setStartError(null);
        try {
            const response = await startCoachingActionPlan({
                employee_number: associate.employee_number,
                author_employee_number: authorEmployeeNumber,
                scorecard_metric_id: metric.scorecard_metric_id,
                coaching_next_best_action_id: associate.coaching_next_best_action_id,
                session_start_date: new Date().toISOString(),
                metric_current_value: metric.metric_value ,
                metric_target_value: metric.target ,
                // metric_current_value: metric.metric_value ?? 0,
                // metric_target_value: metric.target ?? 0,
                corrective_action_id: null,
            }).unwrap();

            window.scrollTo(0, 0);
            sessionStorage.setItem(CAP_NAV_TOKEN_KEY, 'true');
            navigate('/coachingActionPlan', {
                state: {
                    activeStep: 1,
                    coachingActionPlanId: response.data.out_coaching_action_plan_id,
                    coachingActionPlanSessionId: response.data.out_coaching_action_plan_session_id,
                    employeeNumber: associate.employee_number,
                    employeeName: associate.employee_name,
                    scorecardVersionId,
                    scorecardMetricId: metric.scorecard_metric_id,
                    coachingNextBestActionId: associate.coaching_next_best_action_id,
                    metricName: metric.metric_name,
                    metricCurrentValue: metric.metric_value ?? null,
                    metricTargetValue: metric.target ?? null,
                    metricTypeId: metric.metric_type_id,
                    metricTypeName: metric.metric_type_name,
                    metricDirectionId: metric.metric_direction_id,
                    metricIndex: selectedMetricIndex,
                    dailyData: selectedDailyElement ?? null,
                    historyData: filteredHistoryData ?? [],
                    metricStartDate: metric.start_date ?? null,
                    metricEndDate: metric.end_date ?? null,
                },
            });
        } catch {
            setStartError('Failed to start coaching action plan. Please try again.');
        }
    };

    // ── CTA click dispatcher ──────────────────────────────────────────────────
    const handleCtaClick = (metric: PrepareAssociateMetric) => {
        console.log(metric);
        switch (metric.coaching_next_best_action_type_id) {
            case TYPE_START_COACHING:
                if (metric.coaching_action_plan_id && metric.coaching_action_plan_status_id === CAP_STATUS_DRAFT) {
                    navigateToAnalyzeStep(metric);
                } else if (metric.session_count >= 1 && metric.coaching_action_plan_id) {
                    navigateToCoachStep(metric);
                } else {
                    handleStartCoaching(metric);
                }
                break;
            case TYPE_RECOGNIZE:
                if (metric.coaching_action_plan_id) {
                    navigateToFollowUp(metric);
                } else {
                    setRecognizingMetric(metric);
                    setShoutoutVisible(true);
                }
                break;
            case TYPE_FOLLOW_UP:
                navigateToFollowUp(metric);
                break;
            case TYPE_REVIEW:
                if (metric.coaching_action_plan_id && metric.session_count <= 1) {
                    navigateToCoachStep(metric, true);
                } else {
                    navigateToFollowUp(metric);
                }
                break;
            case TYPE_RECOGNIZED:
                break;
            default:
                break;
        }
    };

    const renderConversationLog = () => (
        <NewConversationLog
            setViewState={setViewState}
            setDisplayModal={setShowNewConversationModal}
            viewState={viewState}
            displayModal={showNewConversationModal}
            authorId={loggedUser}
            authorName={loggedEmpUserName}
            recieverId={associate.employee_number}
            receiverName={associate.employee_name}
            via={CoachingEntityTypes.CoachingMetricRecognition}
            date={new Date()}
            coachingEntityId={selectedScorecardMetricId}
            coachingEntityTypeId={CoachingEntityTypes.CoachingMetricRecognition}
        />
    );

    // ── Additional Actions dropdown handler ───────────────────────────────────
    const handleAdditionalAction = (action: string, metric: PrepareAssociateMetric) => {
        setOpenDropdownId(null);
        setDropdownPos(null);
        switch (action) {
            case 'start_coaching':
                handleStartCoaching(metric);
                break;
            case 'recognize':
                setRecognizingMetric(metric);
                setShoutoutVisible(true);
                break;
            case 'create_discussion':
                setViewState('new');
                setShowNewConversationModal(true);
                setSelectedScorecardMetricId(metric.scorecard_metric_id);
                break;
            default:
                break;
        }
    };

    // ── Open dropdown: capture button position for portal ────────────────────
    const handleDropdownToggle = (e: React.MouseEvent<HTMLButtonElement>, metricId: number) => {
        if (openDropdownId === metricId) {
            setOpenDropdownId(null);
            setDropdownPos(null);
        } else {
            const rect = e.currentTarget.getBoundingClientRect();
            setDropdownPos({
                top: rect.bottom + window.scrollY + 4,
                left: rect.right + window.scrollX - 150, // right-align to button
            });
            setOpenDropdownId(metricId);
        }
    };

    // ── Additional Actions menu items per NBA type ────────────────────────────
    const getAdditionalActionItems = (metric: PrepareAssociateMetric): { key: string; label: string }[] => {
        const startCoaching    = { key: 'start_coaching',    label: 'Start Coaching' };
        const recognize        = { key: 'recognize',         label: 'Recognize' };
        const createDiscussion = { key: 'create_discussion', label: 'Create Discussion Log' };

        switch (metric.coaching_next_best_action_type_id) {
            case TYPE_START_COACHING:
                return [recognize, createDiscussion];
            case TYPE_RECOGNIZE:
                return metric.coaching_action_plan_id
                    ? [createDiscussion]
                    : [startCoaching, createDiscussion];
            case TYPE_FOLLOW_UP:
            case TYPE_REVIEW:
                return [createDiscussion];
            case 5: // Insufficient Data
                return [startCoaching, recognize, createDiscussion];
            case TYPE_RECOGNIZED:
                return [startCoaching, createDiscussion];
            default: // TYPE_RECOGNIZED (6) and others
                return [startCoaching, recognize, createDiscussion];
        }
    };

    // ── CTA label & style by type ─────────────────────────────────────────────
    const getCtaLabel = (metric: PrepareAssociateMetric): string => {
        switch (metric.coaching_next_best_action_type_id) {
            case TYPE_RECOGNIZE:      return 'Recognize';
            case TYPE_FOLLOW_UP:      return 'Follow Up';
            case TYPE_REVIEW:         return 'Review';
            case TYPE_RECOGNIZED:     return 'Recognized';
            case TYPE_START_COACHING: return 'Start Coaching';
            default:                  return 'Take Action';
        }
    };

    const getCtaClass = (metric: PrepareAssociateMetric): string => {
        switch (metric.coaching_next_best_action_type_id) {
            case TYPE_RECOGNIZE:  return 'coaching-card__cta coaching-card__cta--recognize';
            case TYPE_RECOGNIZED: return 'coaching-card__cta coaching-card__cta--recognized';
            case TYPE_FOLLOW_UP:
            case TYPE_REVIEW:     return 'coaching-card__cta coaching-card__cta--followup';
            default:              return 'coaching-card__cta';
        }
    };

    const isCtaHidden = (metric: PrepareAssociateMetric): boolean =>
        metric.coaching_next_best_action_type_id === 5;

    // ── Loading / empty ───────────────────────────────────────────────────────
    if (metricsLoading) {
        return (
            <div className="expanded-view-loading">
                <i className="pi pi-spin pi-spinner" />
                <span>Loading metrics...</span>
            </div>
        );
    }

    if (!metrics.length) {
        return (
            <div className="expanded-view-empty">
                <span>No metric data available.</span>
            </div>
        );
    }

    // ── Helpers ───────────────────────────────────────────────────────────────
    const renderActionBadge = (metric: PrepareAssociateMetric) => {
        const badgeClass = `action-badge ${getActionBadgeClass(metric.coaching_next_best_action_type_id)}`;

        if (metric.coaching_next_best_action_type_id === TYPE_RECOGNIZED) {
            return (
                <div>
                    <Tooltip
                        target={`#recognized-cta-grid-${associate.employee_number}-${metric.scorecard_metric_id}`}
                        content="This metric was recently recognized"
                        position="top"
                    />
                    <div id={`recognized-cta-grid-${associate.employee_number}-${metric.scorecard_metric_id}`} className="action-badge action-badge--recognized">
                        <i className="pi pi-check action-badge__icon" />
                        <span>{metric.coaching_next_best_action_type_name}</span>
                    </div>
                </div>
            );
        }

        if (metric.coaching_next_best_action_type_id === 5) {
            return (
                <div className={badgeClass}>
                    <span>{metric.coaching_next_best_action_type_name}</span>
                </div>
            );
        }

        return (
            <div className={badgeClass}>
                <span>{metric.coaching_next_best_action_type_name}</span>
                {readonly === false && (
                    <>
                        <div className="action-badge__separator" />
                        <i className="pi pi-arrow-circle-right action-badge__icon" />
                    </>
                )}
            </div>
        );
    };

    const getActionBadgeClass = (typeId: number) => {
        switch (typeId) {
            case TYPE_START_COACHING: return 'action-badge--coaching';
            case TYPE_RECOGNIZE:      return 'action-badge--recognize';
            case TYPE_RECOGNIZED:     return 'action-badge--recognized';
            case TYPE_FOLLOW_UP:      return 'action-badge--followup';
            case TYPE_REVIEW:         return 'action-badge--review';
            case 5:                   return 'action-badge--insufficient';
            default:                  return 'action-badge--followup';
        }
    };

    // ── The open dropdown's metric (needed to render portal) ─────────────────
    const openMetric = openDropdownId !== null
        ? metrics.find((m) => m.scorecard_metric_id === openDropdownId) ?? null
        : null;

    const isTiered = isTieredScorecard(associate.scorecard_calculation_rule_id);
    const meetsTarget = getMeetsTarget(selectedMetric?.targets as MetricTargetTier[] | null);
    const exceedsTarget = getExceedsTarget(selectedMetric?.targets as MetricTargetTier[] | null);
    const colorIndex = selectedMetricIndex % MetricColors.length;
    const tieredColorPair = TieredMetricColors[colorIndex] ?? TieredMetricColors[0];
    const legacyStatusBadge = selectedMetric?.metric_attained ? (
        <span className="status-badge status-badge--met">
            <i className="pi pi-check-circle" /> Metric Met
        </span>
    ) : (
        <span className="status-badge status-badge--not-met">
            <i className="pi pi-times-circle" /> Metric Not Met
        </span>
    );

    // ── Render ────────────────────────────────────────────────────────────────
    return (
        <div className="associate-expanded-view">

            {/* ── TOP: metrics list (left) + coaching card (right) ── */}
            <div className="expanded-top-section">

                {/* LEFT: All metrics performance */}
                <div className="expanded-metrics-panel">
                    <h6 className="expanded-panel-title">All metrics performance</h6>

                    {additionalMetrics.length > 0 && (
                    <div className="metrics-tab-switcher">
                        <button
                            className={`metrics-tab ${activeTab === 'standard' ? 'metrics-tab--active' : ''}`}
                            onClick={() => setActiveTab('standard')}
                        >
                            <img src={standardMetricsIcon} alt="Standard Metrics" style={{ width: '16px', height: '16px' }} />
                            Standard Metrics
                        </button>
                        <button
                            className={`metrics-tab ${activeTab === 'additional' ? 'metrics-tab--active' : ''} ${additionalMetrics.length === 0 ? 'metrics-tab--disabled' : ''}`}
                            onClick={() => setActiveTab('additional')}
                            disabled={additionalMetrics.length === 0}
                        >
                            <img src={additionalMetricsIcon} alt="Additional Metrics" style={{ width: '16px', height: '16px' }} />
                            Additional Metrics (Non Payout)
                        </button>
                    </div>
                    )}

                    {activeTab === 'additional' && (
                        <div className="metrics-total-row">
                            Total Additional Metrics: <strong>{additionalMetrics.length}</strong>
                        </div>
                    )}

                    <div className="metrics-list" style={{ maxHeight: '320px', overflowY: 'auto' }}>
                        {displayedMetrics.map((metric, index) => {
                            const isSelected = metric.scorecard_metric_id === selectedMetricId;
                            const isRecommended = metric.scorecard_metric_id === associate.scorecard_metric_id;
                            const pillClass = metric.metric_attained ? 'rollup-pill-success' : 'rollup-pill-danger';
                            const isDropdownOpen = openDropdownId === metric.scorecard_metric_id;

                            return (
                                <div
                                    key={metric.coaching_next_best_action_metric_id}
                                    className={`metric-row ${isSelected ? 'metric-row--selected' : ''}`}
                                    onClick={() => { setSelectedMetricId(metric.scorecard_metric_id); setSelectedMetricIndex(index); }}
                                    role="button"
                                    tabIndex={0}
                                    onKeyDown={(e) => {
                                        if (e.key === 'Enter' || e.key === ' ') {
                                            setSelectedMetricId(metric.scorecard_metric_id);
                                            setSelectedMetricIndex(index);
                                        }
                                    }}
                                >
                                    <span className="metric-row__name" style={{ flex: 1 }}>{metric.metric_name}</span>

                                    <div className="metric-row__value-block">
                                        <span className={`rollup-pill rollup-pill--sm ${pillClass}`}>
                                            {metricValueFormat(metric.metric_value, metric.metric_type_name)}
                                        </span>
                                        <span className="metric-row__target">
                                            / {metricValueFormat(metric.target, metric.metric_type_name)}
                                        </span>
                                    </div>

                                    {/* ── Action badge column ── */}
                                    <div className="metric-row__action-block">
                                        {isRecommended && (
                                            <span className="recommended-tag">Recommended</span>
                                        )}
                                        {renderActionBadge(metric)}
                                    </div>

                                    {/* ── Additional Actions column — trigger only, menu via portal ── */}
                                    {readonly === false && (
                                    <div
                                        className="additional-actions-dropdown"
                                        style={{ position: 'relative', flexShrink: 0 }}
                                        role="none"
                                        onClick={(e) => e.stopPropagation()}
                                        onKeyDown={(e) => e.stopPropagation()}
                                    >
                                        <button
                                            className="additional-actions-btn"
                                            onClick={(e) => handleDropdownToggle(e, metric.scorecard_metric_id)}
                                            style={{
                                                borderRadius: '20px',
                                                border: '1px solid #1A5AA5',
                                                color: '#1A5AA5',
                                            }}
                                        >
                                            Additional Actions
                                            <i
                                                className={`pi ${isDropdownOpen ? 'pi-chevron-up' : 'pi-chevron-down'}`}
                                                style={{ marginLeft: '6px', fontSize: '11px', color: '#1A5AA5' }}
                                            />
                                        </button>
                                    </div>)}
                                </div>
                            );
                        })}
                    </div>
                </div>

                {/* RIGHT: Coaching card */}
                {selectedMetric && !readonly && (
                    <div
                        className="expanded-coaching-card"
                        style={{ backgroundColor: MetricColors[selectedMetricIndex % MetricColors.length].bgColor }}
                    >
                        <h6 className="coaching-card__title">Recommended Metric Coaching</h6>
                        <div className="coaching-card__metric-name">{selectedMetric.metric_name}</div>
                        <div className="coaching-card__direction">
                            {getMetricDirectionImage(selectedMetric.metric_direction_id) && (
                                <img
                                    src={getMetricDirectionImage(selectedMetric.metric_direction_id)}
                                    alt="trend"
                                    width={18}
                                    height={18}
                                />
                            )}
                            <span>
                                {selectedMetric.metric_direction_id === 1 ? 'Ascending' : 'Descending'}
                            </span>
                        </div>

                        <div className="coaching-card__donut-wrapper" style={{ position: 'relative' }}>
                            <div style={{
                                width: isTiered ? '200px' : '160px',
                                height: isTiered ? '200px' : '160px',
                                margin: '0 auto',
                                marginBottom: isTiered ? '-85px' : '0',
                                position: 'relative',
                            }}>
                                <MetricGaugeVisual
                                    variant={isTiered ? 'tiered' : 'legacy'}
                                    valueText={metricValueFormat(selectedMetric.metric_value, selectedMetric.metric_type_name)}
                                    colorIndex={colorIndex}
                                    metricOmitted={false}
                                    value={selectedMetric.metric_value ?? undefined}
                                    target={selectedMetric.target ?? undefined}
                                    metricAttained={selectedMetric.metric_attained}
                                    attainmentRank={selectedMetric.attainment_rank ?? null}
                                    attainmentLevelLabel={selectedMetric.metric_attainment_level_label ?? null}
                                    metricValue={selectedMetric.metric_value ?? undefined}
                                    min={selectedMetric.min ?? undefined}
                                    max={selectedMetric.max ?? undefined}
                                    meetsTarget={meetsTarget}
                                    exceedsTarget={exceedsTarget}
                                />
                            </div>
                            {selectedMetric.metric_start_value_override != null && (
                                <>
                                    <i
                                        className="pi pi-info-circle"
                                        data-tooltip-id={`prepare-override-${selectedMetric.scorecard_metric_id}`}
                                        style={{
                                            position: 'absolute',
                                            top: '5px',
                                            right: '0px',
                                            cursor: 'pointer',
                                            fontSize: '30px',
                                            color: '#C0392B',
                                            backgroundColor: '#FADBD8',
                                            borderRadius: '50%',
                                            padding: '4px',
                                        }}
                                    />
                                    <ReactTooltip
                                        id={`prepare-override-${selectedMetric.scorecard_metric_id}`}
                                        className="rounded tooltip override-tooltip"
                                        events={['click']}
                                        place="right-start"
                                        closeOnEsc
                                        positionStrategy="fixed"
                                        style={{ overflow: 'visible', maxHeight: 'none', zIndex: 99999 }}
                                    >
                                        <div className="override-tooltip__content">
                                            <span className="override-tooltip__label">Overridden Data</span>
                                            <span className="override-tooltip__value">
                                                {metricValueFormat(selectedMetric.metric_start_value_override, selectedMetric.metric_type_name)}
                                            </span>
                                            <span className="override-tooltip__description">
                                                {selectedMetric.metric_start_value_override_reason ?? ''}
                                            </span>
                                        </div>
                                    </ReactTooltip>
                                </>
                            )}
                        </div>

                        <div className="coaching-card__status">
                            {isTiered ? (
                                <span style={{
                                    fontWeight: 600,
                                    color: getTieredStatusVisual(
                                        selectedMetric.metric_attainment_level_label,
                                        selectedMetric.attainment_rank
                                    ).color,
                                }}>
                                    {getTieredStatusVisual(
                                        selectedMetric.metric_attainment_level_label,
                                        selectedMetric.attainment_rank
                                    ).icon === 'cross'
                                        ? <i className="pi pi-times-circle" />
                                        : <i className="pi pi-check-circle" />
                                    }
                                    {' '}{selectedMetric.metric_attainment_level_label ?? '--'}
                                </span>
                            ) : legacyStatusBadge}
                        </div>

                        {isTiered ? (
                            <div style={{ textAlign: 'center', marginTop: '4px' }}>
                                {meetsTarget !== null && (
                                    <div style={{ fontSize: '0.8rem', color: tieredColorPair.dark, display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '4px' }}>
                                        <TieredTargetIcon />
                                        <span>Meets at: {metricValueFormat(meetsTarget, selectedMetric.metric_type_name)}</span>
                                    </div>
                                )}
                                {exceedsTarget !== null && (
                                    <div style={{ fontSize: '0.8rem', color: tieredColorPair.light, display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '4px' }}>
                                        <TieredTargetIcon />
                                        <span>Exceeds at: {metricValueFormat(exceedsTarget, selectedMetric.metric_type_name)}</span>
                                    </div>
                                )}
                            </div>
                        ) : (
                            // Legacy model: single Target
                            <div className="coaching-card__target">
                                <i className="pi pi-bullseye" />
                                <span>Target: {metricValueFormat(selectedMetric.target, selectedMetric.metric_type_name)}</span>
                            </div>
                        )}

                        {startError && (
                            <span className="coaching-card__error">{startError}</span>
                        )}

                        {!isCtaHidden(selectedMetric) && (
                            selectedMetric.coaching_next_best_action_type_id === TYPE_RECOGNIZED ? (
                                <>
                                    <Tooltip
                                        target={`#recognized-cta-${associate.employee_number}`}
                                        content="This metric was recently recognized"
                                        position="top"
                                    />
                                    <div
                                        id={`recognized-cta-${associate.employee_number}`}
                                        className="recommendation-badge recommendation-badge-recognized"
                                        style={{ width: '100%', justifyContent: 'center', marginTop: '0.5rem' }}
                                    >
                                        <i className="pi pi-check recommendation-icon" />
                                        <span>{getCtaLabel(selectedMetric)}</span>
                                    </div>
                                </>
                            ) : (
                                <button
                                    className={getCtaClass(selectedMetric)}
                                    onClick={() => handleCtaClick(selectedMetric)}
                                    disabled={isStarting}
                                >
                                    {isStarting
                                        ? <i className="pi pi-spin pi-spinner" />
                                        : <i className="pi pi-arrow-circle-right" />
                                    }
                                    <span>{getCtaLabel(selectedMetric)}</span>
                                </button>
                            )
                        )}
                    </div>
                )}
            </div>

            {/* ── BOTTOM: time series bar graph ── */}
            {selectedMetric && (selectedDailyElement || (filteredHistoryData && filteredHistoryData.length > 0)) && (
                <div className="expanded-graph-section">
                    <MetricTimeSeriesBarGraph
                        metricName={selectedDailyElement?.label ?? selectedMetric.metric_name}
                        metricId={selectedDailyElement?.metric_id ?? 0}
                        scorecardVersionId={scorecardVersionId}
                        employeeNumber={associate.employee_number}
                        target={isTiered ? (meetsTarget ?? selectedMetric.target) : (selectedDailyElement?.target ?? selectedMetric.target)}
                        metricTypeName={selectedDailyElement?.metric_type_name ?? selectedMetric.metric_type_name}
                        dailyData={selectedDailyElement?.dailyMetricSummaryList ?? []}
                        metricDirectionId={selectedDailyElement?.metric_direction_id ?? selectedMetric.metric_direction_id}
                        calculatedDate={selectedDailyElement?.calculate_date ?? null}
                        metricHistoryData={filteredHistoryData}
                        fromMetricDashboard
                        metricIndex={selectedMetricIndex}
                        metricOmitted={false}
                        currentMonthLabel={'Last 30 Days'}
                        historyLabel={'Last 6 Months'}
                        exceedsTarget={isTiered ? exceedsTarget : undefined}
                    />
                </div>
            )}

            {/* ── Shout Out popup ── */}
            {shoutoutVisible && (
                <CoachingShoutOut
                    receiverId={associate.employee_number}
                    receiverName={associate.employee_name}
                    visible={shoutoutVisible}
                    setVisible={setShoutoutVisible}
                    // coachingEntityId={recognizingMetric?.coaching_action_plan_id??undefined}
                    coachingEntityId={recognizingMetric?.scorecard_metric_id}
                    scorecardMetricId={recognizingMetric?.scorecard_metric_id}
                    coachingEntityTypeId={CoachingEntityTypes.CoachingMetricRecognition}
                    coreValueId={0}
                    via={CoachingTabs.GamePlan}
                    result={setShoutoutResult}
                />
            )}

            {showNewConversationModal && renderConversationLog()}

            {/* ── Additional Actions portal menu — rendered outside scroll container ── */}
            {openMetric && dropdownPos && !readonly &&createPortal(
                <div
                    ref={dropdownRef}
                    className="additional-actions-menu"
                    style={{
                        position: 'absolute',
                        top: dropdownPos.top,
                        left: dropdownPos.left,
                        zIndex: 9999,
                    }}
                >
                    {getAdditionalActionItems(openMetric).map((item, i, arr) => (
                        <React.Fragment key={item.key}>
                            <button
                                className="additional-actions-menu__item"
                                onClick={() => handleAdditionalAction(item.key, openMetric)}
                            >
                                {item.label}
                            </button>
                            {i < arr.length - 1 && (
                                <hr className="additional-actions-menu__separator" />
                            )}
                        </React.Fragment>
                    ))}
                </div>,
                document.body
            )}
        </div>
    );
};